/**
 * 부팅 씬.
 * 1) public/assets 오버라이드 탐지 → 2) 텍스처 준비(파일 or 코드 생성)
 * → 3) 애니메이션 등록 → 4) 세이브 로드 → 타이틀로 전환
 */

import Phaser from 'phaser';
import { registerAnimations } from '../assets/animations';
import { detectOverrides, queueOverrides, resolveAssets, type AssetTable } from '../assets/loader';
import { hydrateGameData } from '../data/hydrate';
import { PlayerState } from '../systems/PlayerState';
import { SaveGame } from '../systems/SaveGame';
import { TEXT_COLOR, UI_FONT } from '../ui/theme';

export const REG = {
  assets: 'assets',
  state: 'state',
} as const;

export class BootScene extends Phaser.Scene {
  constructor() {
    super('boot');
  }

  create(): void {
    const { width, height } = this.scale;
    this.add
      .text(width / 2, height / 2 - 16, '메이플 감성 횡스크롤 습작', {
        fontFamily: UI_FONT,
        fontSize: '22px',
        color: TEXT_COLOR.bright,
        align: 'center',
        wordWrap: { width: width - 64, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    const status = this.add
      .text(width / 2, height / 2 + 22, '에셋 준비 중...', {
        fontFamily: UI_FONT,
        fontSize: '13px',
        color: TEXT_COLOR.dim,
        align: 'center',
        wordWrap: { width: width - 64, useAdvancedWrap: true },
      })
      .setOrigin(0.5);

    void this.boot(status);
  }

  private async boot(status: Phaser.GameObjects.Text): Promise<void> {
    const { pending } = await detectOverrides();

    if (pending.length > 0) {
      status.setText(`교체 에셋 ${pending.length}개 불러오는 중...`);
      queueOverrides(this, pending);
      await new Promise<void>((resolve) => {
        this.load.once(Phaser.Loader.Events.COMPLETE, () => resolve());
        this.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
          /* 개별 실패는 폴백으로 처리 */
        });
        this.load.start();
      });
    }

    status.setText('데이터 확인 중...');
    const dataSource = await hydrateGameData();
    console.info(`[data] ${dataSource === 'file' ? 'public/data JSON' : '번들 JSON'}`);

    status.setText('도트 아트 생성 중...');
    const assets = resolveAssets(this, pending);
    registerAnimations(this, assets);

    // 세이브 저장소를 먼저 준비한다 (데스크톱=파일, 브라우저=localStorage)
    status.setText('세이브 확인 중...');
    await SaveGame.init();
    console.info(`[save] 저장 위치: ${SaveGame.location} (${SaveGame.backend})`);

    const summary = assets.summary();
    console.info(`[assets] 교체 ${summary.file} / 기본 ${summary.generated}`);
    this.exposeDebug(assets);

    const state = new PlayerState();
    this.registry.set(REG.assets, assets);
    this.registry.set(REG.state, state);

    this.scene.start('title');
  }

  private exposeDebug(assets: AssetTable): void {
    (window as unknown as { __assets: unknown }).__assets = assets
      .all()
      .map((a) => ({ key: a.key, source: a.source, frames: a.frames, size: `${a.frameWidth}x${a.frameHeight}`, file: a.file }));
  }
}
